import React, { forwardRef, useState } from "react";
import { Link, navigate } from "../components/Router";
import Autosuggest from "react-autosuggest";
import AutosuggestHighlightMatch from "autosuggest-highlight/match";
import AutosuggestHighlightParse from "autosuggest-highlight/parse";
import searchIcon from "../img/search.svg";
import { companyLink } from "../components/Links.mjs";
import { getAllSymbols, isAllSymbolsLoaded } from "../components/Symbols.mjs";
import linkArrowImage from "img/link-arrow.svg";
import { recordEvent } from "../components/Analytics";
import Spinner from "react-bootstrap/Spinner";
import { AiOutlineWarning } from "react-icons/ai";
import { CLOUD_FRONT_ABSOLUTE_ENDPOINT, IS_ALPHA_STAGE } from "api-config.mjs";
import { CompanyLogoDiv } from "./Logo";
import log from "loglevel";

function getSuggestionValue(suggestion) {
  return `${suggestion.symbol} - ${suggestion.name}`;
}

function autoHighlightParseIt(text, query) {
  const matches = AutosuggestHighlightMatch(text, query || "");
  return AutosuggestHighlightParse(text, matches);
}

function getHighLightedText(matches) {
  // log.debug("matches:", matches);
  return (
    <>
      {matches.map((part, index) => {
        const className = part.highlight ? "highlight" : null;
        return (
          <span className={className} key={index}>
            {part.text}
          </span>
        );
      })}
    </>
  );
}

const StatusContainer = ({ value, loadingSymbols, noSuggestions }) => {
  return (
    <>
      {(loadingSymbols || noSuggestions) && (
        <div className="status-container">
          <div className="status">
            <div className="inner-container">
              {loadingSymbols && (
                <>
                  <div className="dots-3"></div>
                </>
              )}
              {noSuggestions && (
                <>
                  <AiOutlineWarning className="me-1" />
                  Nothing found for "{value}"
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const RenderSuggestionItem = ({ exchange, symbol, name, query }) => {
  const symbolMatches = autoHighlightParseIt(symbol, query);
  const nameMatches = autoHighlightParseIt(name, query);
  const companyUrl = companyLink(exchange, symbol);
  // log.debug(symbol);

  return (
    <Link to={companyUrl} data-prefetch="true" className="suggest-item">
      <CompanyLogoDiv {...{ exchange, symbol, divClass: "cp-image" }} />
      <div className="cp-info">
        <div className="name-and-label">
          <span className="name">{getHighLightedText(nameMatches)}</span>

          <span className="lbl">{getHighLightedText(symbolMatches)}</span>
        </div>
        <div className="total-call invisible">135 Calls</div>
      </div>

      <div className="icon">
        <img src={linkArrowImage} alt="icon" />
      </div>
    </Link>
  );
};

const RenderSuggestionItemWithoutLink = ({ exchange, symbol, name, query }) => {
  const symbolMatches = autoHighlightParseIt(symbol, query);
  const nameMatches = autoHighlightParseIt(name, query);
  // log.debug(symbol);

  return (
    <span className="suggest-item">
      <CompanyLogoDiv {...{ exchange, symbol, divClass: "cp-image" }} />
      <div className="cp-info">
        <div className="name-and-label">
          <span className="lbl">
            {exchange}:{getHighLightedText(symbolMatches)}
          </span>
          <span className="name">{getHighLightedText(nameMatches)}</span>
        </div>
        <div className="total-call invisible">135 Calls</div>
      </div>

      <div className="icon">
        <img src={linkArrowImage} alt="icon" />
      </div>
    </span>
  );
};

const filterSymbols = (value, symbols, config = {}) => {
  const { numberSymbolMatches = 3, numberNameMatches = 5 } = config;
  let exactSymbolMatches = [];
  let symbolMatches = [];
  let nameMatches = [];
  const valueLower = value.toLowerCase();
  symbols.forEach((_symbol) => {
    // log.debug(_symbol);
    const { symbol, name } = _symbol;
    const symbolLower = symbol.toLowerCase();
    const nameLower = name.toLowerCase();
    if (valueLower === symbolLower) {
      exactSymbolMatches.push(_symbol);
    } else if (symbolLower.includes(valueLower)) {
      symbolMatches.push(_symbol);
    } else if (nameLower.includes(valueLower)) {
      nameMatches.push(_symbol);
    }
  });
  // log.debug(exactSymbolMatches);
  let result = exactSymbolMatches.splice(0, numberSymbolMatches);
  if (result.length < numberSymbolMatches) {
    result = result.concat(
      symbolMatches.splice(0, numberSymbolMatches - result.length)
    );
  }
  result = result.concat(nameMatches.splice(0, numberNameMatches));

  return result;
};

// Use this function to render custom suggestions container.
const renderSuggestionsContainer = ({ containerProps, children, query }) => {
  log.trace("renderSuggestionsContainer:", containerProps);
  return (
    <div {...containerProps}>
      {/* <h2>hi</h2> */}
      {children}
    </div>
  );
};

const RenderInputComponent = (inputProps) => (
  <>
    <input {...inputProps} />
  </>
);

const RenderInputComponentWithSearchButton = (inputProps) => (
  <>
    <input {...inputProps} />
    <button className="btn btn-primary" type="submit" id="button-search-addon">
      <span className="icon">
        <img src={searchIcon} alt="icon" />
      </span>
      <span className="txt">Search</span>
    </button>
  </>
);

const symbolConfig = {
  endpoint: CLOUD_FRONT_ABSOLUTE_ENDPOINT,
  symbolsFile: "symbols-v2.json",
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const SearchBar = forwardRef(
  (
    {
      value: initialValue,
      onChange,
      placeholder,
      required = false,
      id = "defaultSearchBar",
      inputClassName,
      renderForm = true,
      renderSearchButton = true,
      renderLinks = true,
    },
    ref
  ) => {
    const buildVersion = "1"; // TODO FIXME
    // const { buildVersion } = useRouteData();
    const [value, setValue] = useState(initialValue || "");
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSymbols, setLoadingSymbols] = useState(false);
    const noSuggestions = !loadingSymbols && value && suggestions.length === 0;

    const onChangeEvent = (event, { newValue }) => {
      log.trace(`onChange(${event})`);
      if (onChange) {
        onChange(newValue);
      }
      setValue(newValue);
    };

    const loadSuggestions = async (query) => {
      log.debug(`loadSuggestions(${query})`);
      if (!isAllSymbolsLoaded()) {
        setLoadingSymbols(true);
        if (IS_ALPHA_STAGE) {
          await sleep(300); // Artificial delay to show loading indicator, used for development only
        }
      }
      const symbols = await getAllSymbols();
      setLoadingSymbols(false);
      const suggestions = filterSymbols(query, symbols);
      setSuggestions(suggestions);
      recordEvent({
        name: "SearchBarQuery",
        query,
        resultsSize: suggestions.length,
        buildVersion,
      }); // FIXME TODO: buildVersion hard-coded
      return suggestions;
    };

    const onSuggestionsFetchRequested = ({ value }) => {
      log.trace(`onSuggestionsFetchRequested()`);
      loadSuggestions(value);
    };

    const onSuggestionsClearRequested = () => {
      log.trace(`onSuggestionsClearRequested()`);
    };

    const onSuggestionSelected = (
      event,
      { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }
    ) => {
      log.trace("onSuggestionSelected", event);
      log.debug(
        "onSuggestionSelected",
        suggestion,
        suggestionValue,
        suggestionIndex
      );
      if (renderLinks) {
        // Prevent a flash of text showing in the input element before the new link is navigated to..
        setValue("");
      }
    };

    const encodeGetParams = (p) =>
      Object.entries(p)
        .map((kv) => kv.map(encodeURIComponent).join("="))
        .join("&");

    const searchSubmit = (event) => {
      event.preventDefault();
      log.info("searchSubmit:", event);
      const searchParams = {
        q: value,
      };
      navigate(`/search#${encodeURIComponent(value)}`);
    };

    log.trace(
      "SearchBar render() state value:",
      value,
      "props value:",
      initialValue
    );
    log.debug("SearchBar value:", value);

    const inputProps = {
      placeholder,
      value,
      onChange: onChangeEvent,
      required,
      className: inputClassName,
      ref,
    };

    // https://github.com/moroshko/react-autosuggest#theme-prop
    const theme = {
      container: "input-group search-input",
      containerOpen: "",
      input: "",
      inputOpen: "",
      inputFocused: "",
      suggestionsContainer: "suggestions-container",
      suggestionsContainerOpen: "suggestion-section",
      suggestionsList: "list-group",
      suggestion: "list-group-item",
      suggestionFirst: "",
      suggestionHighlighted: "",
      sectionContainer: "",
      sectionContainerFirst: "",
      sectionTitle: "",
    };
    if (renderForm) {
      return (
        <>
          <form onSubmit={searchSubmit}>
            <Autosuggest
              suggestions={suggestions}
              onSuggestionsFetchRequested={onSuggestionsFetchRequested}
              onSuggestionsClearRequested={onSuggestionsClearRequested}
              onSuggestionSelected={onSuggestionSelected}
              getSuggestionValue={getSuggestionValue}
              renderSuggestionsContainer={renderSuggestionsContainer}
              renderSuggestion={
                renderLinks
                  ? RenderSuggestionItem
                  : RenderSuggestionItemWithoutLink
              }
              renderInputComponent={
                renderSearchButton
                  ? RenderInputComponentWithSearchButton
                  : RenderInputComponent
              }
              // Set this to true if you'd like to render suggestions even when the input is not focused. (for testing)
              alwaysRenderSuggestions={false}
              inputProps={inputProps}
              id={id}
              theme={theme}
            />

            <StatusContainer
              value={value}
              loadingSymbols={loadingSymbols}
              noSuggestions={noSuggestions}
            />
          </form>
          <div className="clear-input-btn">
            <button
              type="button"
              onClick={() => setValue("")}
              className="btn"
              style={{ visibility: value ? "visible" : "hidden" }}
            >
              X
            </button>
          </div>
        </>
      );
    }
    return (
      <>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          onSuggestionSelected={onSuggestionSelected}
          getSuggestionValue={getSuggestionValue}
          renderSuggestionsContainer={renderSuggestionsContainer}
          renderSuggestion={
            renderLinks ? RenderSuggestionItem : RenderSuggestionItemWithoutLink
          }
          renderInputComponent={
            renderSearchButton
              ? RenderInputComponentWithSearchButton
              : RenderInputComponent
          }
          // Set this to true if you'd like to render suggestions even when the input is not focused. (for testing)
          alwaysRenderSuggestions={false}
          inputProps={inputProps}
          id={id}
          theme={theme}
        />

        <StatusContainer
          value={value}
          loadingSymbols={loadingSymbols}
          noSuggestions={noSuggestions}
        />
      </>
    );
  }
);

export {
  getHighLightedText,
  autoHighlightParseIt,
  filterSymbols,
  RenderSuggestionItem,
};
export default SearchBar;
